import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Text, Card, Spinner } from '../../../common/components';
import { useBuildingLogs } from '../../hooks/useBuildingLogs';
import { groupBuildingLogsByChangeStatus, createTimeline } from '../../utils/registryDataProcessor';
import ChangeGroup from './ChangeGroup';
import NoChangeGroup from './NoChangeGroup';
import YearGroup from './YearGroup';

interface BuildingLogsTimelineProps {
  filters?: {
    units?: string[];
    profiles?: string[];
  };
  /** Vlastní obsah pro stav bez záznamů (default: jednoduchá karta s textem). */
  emptyState?: React.ReactNode;
}

type TimelineItem = ReturnType<typeof createTimeline>[number];

// Rok položky = rok jejího novějšího konce (u období "beze změny" je startDate novější datum).
const getItemYear = (item: TimelineItem): number =>
  new Date(item.type === 'change' ? item.date! : item.startDate!).getFullYear();

const BuildingLogsTimeline: React.FC<BuildingLogsTimelineProps> = ({ filters = {}, emptyState }) => {
  const { buildingLogs, isLoading, isLoadingMore, error, hasMore, loadMoreLogs, clearError } = useBuildingLogs(filters);

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const observerRef = useRef<HTMLDivElement>(null);

  const toggleGroupExpand = (groupKey: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  // Intersection Observer pro lazy loading
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasMore && !isLoadingMore && !isLoading) {
        loadMoreLogs();
      }
    },
    [hasMore, isLoadingMore, isLoading, loadMoreLogs]
  );

  useEffect(() => {
    const element = observerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [handleObserver]);

  if (isLoading && buildingLogs.length === 0) {
    return (
      <div className="BuildingLogsContainer--loading">
        <Text>Načítání...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <Text>Chyba při načítání: {error}</Text>
        <button onClick={clearError} className="Button Button--secondary mt-2">
          Zkusit znovu
        </button>
      </Card>
    );
  }

  if (buildingLogs.length === 0) {
    return (
      <>
        {emptyState ?? (
          <Card>
            <Text>Žádná historie kontrol k zobrazení</Text>
          </Card>
        )}
      </>
    );
  }

  const groupedData = groupBuildingLogsByChangeStatus(buildingLogs);
  const timeline = createTimeline(groupedData);

  // Pojistka: logy existují, ale nevznikla z nich žádná zobrazitelná položka
  if (timeline.length === 0) {
    return (
      <>
        {emptyState ?? (
          <Card>
            <Text>Žádná historie kontrol k zobrazení</Text>
          </Card>
        )}
      </>
    );
  }

  const renderItem = (item: TimelineItem) => {
    if (item.type === 'change') {
      return <ChangeGroup key={item.key} date={item.date!} logs={item.logs} />;
    }
    return (
      <NoChangeGroup
        key={item.key}
        startDate={item.startDate!}
        endDate={item.endDate!}
        logs={item.logs}
        isExpanded={!!expandedGroups[item.key]}
        toggleExpand={() => toggleGroupExpand(item.key)}
      />
    );
  };

  // Detailně jen aktuální rok; starší roky sbalené do jednořádkových skupin (timeline je řazená sestupně).
  const currentYear = new Date().getFullYear();
  const currentYearItems = timeline.filter((item) => getItemYear(item) >= currentYear);

  const olderYears: { year: number; items: TimelineItem[]; changeCount: number }[] = [];
  timeline
    .filter((item) => getItemYear(item) < currentYear)
    .forEach((item) => {
      const year = getItemYear(item);
      let group = olderYears.find((g) => g.year === year);
      if (!group) {
        group = { year, items: [], changeCount: 0 };
        olderYears.push(group);
      }
      group.items.push(item);
      if (item.type === 'change') {
        group.changeCount += item.logs.length;
      }
    });

  return (
    <>
      <div className="BuildingLogsContainer">
        {currentYearItems.map(renderItem)}

        {olderYears.map((group) => (
          <YearGroup
            key={`year-${group.year}`}
            year={group.year}
            changeCount={group.changeCount}
            isExpanded={!!expandedGroups[`year-${group.year}`]}
            toggleExpand={() => toggleGroupExpand(`year-${group.year}`)}
          >
            {group.items.map(renderItem)}
          </YearGroup>
        ))}
      </div>

      {/* Intersection Observer target pro lazy loading */}
      {hasMore && (
        <div ref={observerRef} className="LoadMoreObserver" style={{ height: '20px', margin: '20px 0' }}>
          {isLoadingMore && (
            <div className="text-center">
              <Spinner size="small" />
            </div>
          )}
        </div>
      )}

      {!hasMore && buildingLogs.length > 0 && (
        <div className="text-center mt-3 mb-3">
          <Text size="body-small" color="Neutral600">
            Načteny všechny záznamy
          </Text>
        </div>
      )}
    </>
  );
};
export default BuildingLogsTimeline;
